/*
 * File: GetCourseEnrollment.ts
 * Author: Austin Robinson
 * Date Created: 4/27/2025
 * Copyright (c) DrivingDoc 2025
 */

import { createQuery } from 'react-query-kit';
import { PostGreSQLInterval, privateApi } from 'api';

export interface GetCourseEnrollmentParams {
    id: string
}

export interface GetCourseEnrollmentResult {
    tenant_id: string;
    id: string;
    student_user_id: string;
    course_section_id: string;
    student_charge_id: string | null;
    package_course_package_id: string | null;
    package_course_course_id: string | null;
    secure_seat_mode: "at_time_of_enrollment" | "when_deposit_paid" | "when_full_cost_paid" | null;
    seat_secured_at: string | null;
    seat_secured_by: string | null;
    enrolled_at: string;
    created_by: string | null;
    completed_at: string | null;
    tenant_location_id: string | null;
    performance_time_included: PostGreSQLInterval | null;
    observation_time_included: PostGreSQLInterval | null;
    requisite_group: number;
    became_eligible_at: string | null;
    must_complete_by: string | null;
    performance_time_completed: PostGreSQLInterval | null;
    performance_time_scheduled: PostGreSQLInterval | null;
    observation_time_completed: PostGreSQLInterval | null;
    observation_time_scheduled: PostGreSQLInterval | null;
    course_completion_criteria: {
        tenant_id: string;
        course_id: string;
        updated_at: Date;
        performance_time_required?: PostGreSQLInterval | null;
        observation_time_required?: PostGreSQLInterval | null;
        updated_by?: string | null;
    };
    course_policy: {
        tenant_id: string;
        course_id: string;
        updated_at: Date;
        updated_by?: string | null;
        min_time_to_complete?: string | null;
        max_time_to_complete?: string | null;
        allow_self_service_extensions: boolean;
        standard_extension_time?: string | null;
        standard_extension_fee?: number | null;
        max_extensions?: number | null;
        max_time_to_request_extension?: string | null;
        absences_allowed_before_fee?: number | null;
        absence_fee?: number | null;
        absence_fee_due?: string | null;
        absence_fee_due_lock?: string | null;
        no_permits_allowed_before_fee?: number | null;
        no_permit_fee?: number | null;
        no_permit_fee_due?: string | null;
        no_permit_fee_due_lock?: string | null;
        allow_cancellations: boolean;
        cancellations_allowed_before_fee?: number | null;
        cancellation_fee?: number | null;
        cancellation_fee_due?: string | null;
        cancellation_fee_due_lock?: string | null;
        late_cancellations_allowed_before_fee?: number | null;
        late_cancellation_fee?: number | null;
        late_cancellation_fee_due?: string | null;
        late_cancellation_fee_due_lock?: string | null;
        late_cancellation_deadline?: string | null;
    };
}

async function getCourseEnrollment({ id }: GetCourseEnrollmentParams): Promise<GetCourseEnrollmentResult> {
    // make api call
    const result = await privateApi.get(`${import.meta.env.VITE_API_BASE_URL}/courses/enrollments/${id}`);

    // return the data object
    return result.data as GetCourseEnrollmentResult;
}

const useGetCourseEnrollment = createQuery<GetCourseEnrollmentResult, GetCourseEnrollmentParams, Error>({
    queryKey: ['course_enrollment'],
    fetcher: (variables) => getCourseEnrollment(variables),
})

export { getCourseEnrollment, useGetCourseEnrollment };

